import { inject, Injector } from '@angular/core';
import type { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { RedirectCommand, Router } from '@angular/router';
import type { OnyxHttpErrorResponse } from '@onyx/angular';
import { OnyxToastService } from '@onyx/angular';
import { catchError, map, of } from 'rxjs';
import { VALIDATION_ERROR_CODES } from '../../../../common/constants/common/validation-error-codes';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { DelegationFormResolverData } from '../../delegations.routes';
import type { DelegationStatus } from '../enums/delegation-status';
import { DelegationsRoute } from '../enums/delegations-route';
import { DelegationFormHelper } from '../helpers/delegation-form.helper';
import { DelegationHelper } from '../helpers/delegation.helper';
import { DelegationsService } from '../services/delegations.service';

export const editDelegationResolver: ResolveFn<
  DelegationFormResolverData & { status: DelegationStatus }
> = (route: ActivatedRouteSnapshot) => {
  const delegationService = inject(DelegationsService);
  const toastService = inject(OnyxToastService);
  const injector = inject(Injector);
  const router = inject(Router);

  const uuid = route.paramMap.get('uuid')!;
  const urlTree = router.parseUrl(DelegationsRoute.DELEGATIONS_LIST);

  return delegationService.getDelegation(uuid).pipe(
    map((dto) => {
      if (!DelegationHelper.hasEdit(dto)) {
        toastService.showError({
          message: 'toasts.archived',
          keepOnNavigation: true,
        });
        return new RedirectCommand(urlTree, { replaceUrl: true });
      }

      return {
        form: DelegationFormHelper.fromDto(dto),
        initial: DelegationFormHelper.fromDto(dto),
        status: dto.status.value,
      };
    }),
    catchError((response: OnyxHttpErrorResponse) => {
      if (VALIDATION_ERROR_CODES.includes(response.status)) {
        toastService.showError({
          message: 'delegations.delegationNotFound',
          keepOnNavigation: true,
        });
        response.handled = true;
      } else {
        ValidationHelper.handleUnexpectedResponse(response, injector, {
          keepOnNavigation: true,
        });
      }

      return of(new RedirectCommand(urlTree, { replaceUrl: true }));
    }),
  );
};
